/**
 * Hook for accessing local page assets (images from src/assets/page-images/)
 *
 * These assets were migrated from Contentful and are used by hardcoded page components.
 * Asset filenames are derived from Contentful titles (kebab-case with extension).
 *
 * Usage:
 *   const { getAsset } = usePageAssets()
 *   const heroImage = getAsset("home-hero-illustration")
 *   // Returns: { gatsbyImageData, url, description } - compatible with Image component
 */
import { graphql, useStaticQuery } from "gatsby"

export function usePageAssets() {
  const data = useStaticQuery(graphql`
    query PageAssetsQuery {
      # Raster images (PNG, JPG, etc.) - use childImageSharp
      rasterImages: allFile(
        filter: {
          sourceInstanceName: { eq: "images" }
          relativeDirectory: { eq: "page-images" }
          extension: { nin: ["svg"] }
        }
      ) {
        nodes {
          name
          extension
          publicURL
          childImageSharp {
            gatsbyImageData(quality: 100, placeholder: BLURRED)
          }
        }
      }
      # SVG files - only need publicURL (no childImageSharp)
      svgImages: allFile(
        filter: {
          sourceInstanceName: { eq: "images" }
          relativeDirectory: { eq: "page-images" }
          extension: { eq: "svg" }
        }
      ) {
        nodes {
          name
          extension
          publicURL
        }
      }
    }
  `)

  const assets = [...data.rasterImages.nodes, ...data.svgImages.nodes]

  /**
   * Get an asset by its base name (without extension)
   * Returns an object compatible with the Image component:
   * - For raster images: { gatsbyImageData, description }
   * - For SVGs: { url, description }
   * @param {string} name - Asset name (e.g., "home-hero-illustration")
   * @param {string} [description] - Optional alt text description
   * @returns {object | null}
   */
  const getAsset = (name, description = "") => {
    const asset = assets.find((a) => a.name === name)
    if (!asset) {
      console.warn(`[usePageAssets] Asset not found: ${name}`)
      return null
    }

    // SVGs don't have childImageSharp - return url for <img> tag
    if (asset.extension === "svg") {
      return {
        url: asset.publicURL,
        description,
      }
    }

    // Raster images - return gatsbyImageData for GatsbyImage
    return {
      gatsbyImageData: asset.childImageSharp?.gatsbyImageData,
      description,
    }
  }

  /**
   * Get multiple assets by their base names
   * @param {string[]} names - Array of asset names
   * @returns {Object.<string, { gatsbyImageData?: object, publicURL: string, extension: string }>}
   */
  const getAssets = (names) => {
    const result = {}
    names.forEach((name) => {
      result[name] = getAsset(name)
    })
    return result
  }

  return { getAsset, getAssets, assets }
}
